import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useContent } from '../../../../contexts/ContentContext';
import { useAuth } from '../../../../contexts/AuthContext';

const AUTHORS = [
  { name: '张晓明', avatar: 'https://readdy.ai/api/search-image?query=young%20asian%20male%20designer%20portrait%20glasses%20confident%20expression%20modern%20office%20background%20natural%20lighting%20high%20quality%20professional%20headshot&width=100&height=100&seq=author2&orientation=squarish' },
  { name: '林小雨', avatar: 'https://readdy.ai/api/search-image?query=Professional%20young%20female%20designer%20portrait%20friendly%20smile%20creative%20workspace%20background%20natural%20lighting%20high%20quality%20professional%20headshot&width=100&height=100&seq=author1&orientation=squarish' },
  { name: '王小美', avatar: 'https://readdy.ai/api/search-image?query=Professional%20young%20female%20creative%20designer%20portrait%20artistic%20style%20colorful%20background%20natural%20lighting%20high%20quality%20professional%20headshot&width=100&height=100&seq=author3&orientation=squarish' },
  { name: '陈思远', avatar: 'https://readdy.ai/api/search-image?query=young%20asian%20male%20student%20portrait%20casual%20style%20clean%20background%20confident%20expression%20modern%20look&width=100&height=100&seq=author4&orientation=squarish' },
];

const CATEGORIES = ['图像处理', '数字媒体设计', 'AI创作', '工具技巧', '项目复盘', '学习总结'];

export default function ArticleEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addArticle, updateArticle, getArticleById } = useContent();
  const { user } = useAuth();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    category: CATEGORIES[0],
    coverImage: '',
    author: AUTHORS[0],
    publishDate: new Date().toISOString().split('T')[0],
    readTime: 10,
    tags: [] as string[],
    content: '',
  });

  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (isEdit && id) {
      const article = getArticleById(Number(id));
      if (article) {
        setFormData({
          title: article.title,
          summary: article.summary,
          category: article.category,
          coverImage: article.coverImage,
          author: article.author,
          publishDate: article.publishDate,
          readTime: article.readTime,
          tags: article.tags,
          content: '',
        });
      }
    }
  }, [id, isEdit, getArticleById]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const articleData = {
      ...formData,
      likes: 0,
      bookmarks: 0,
      comments: 0,
    };

    if (isEdit && id) {
      updateArticle(Number(id), articleData);
    } else {
      addArticle(articleData);
    }

    navigate('/admin/articles');
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag),
    }));
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin/articles')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <i className="ri-arrow-left-line"></i>
          返回文章列表
        </button>
        <h1 className="text-3xl font-bold text-gray-900">{isEdit ? '编辑文章' : '新建文章'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              文章标题 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="输入文章标题"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              文章摘要 <span className="text-red-500">*</span>
            </label>
            <textarea
              required
              value={formData.summary}
              onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))}
              rows={3}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
              placeholder="输入文章摘要"
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                分类 <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                作者 <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.author.name}
                onChange={(e) => {
                  const author = AUTHORS.find(a => a.name === e.target.value);
                  if (author) {
                    setFormData(prev => ({ ...prev, author }));
                  }
                }}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
              >
                {AUTHORS.map(author => (
                  <option key={author.name} value={author.name}>{author.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                发布日期 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                required
                value={formData.publishDate}
                onChange={(e) => setFormData(prev => ({ ...prev, publishDate: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                阅读时长（分钟）
              </label>
              <input
                type="number"
                min="1"
                value={formData.readTime}
                onChange={(e) => setFormData(prev => ({ ...prev, readTime: Number(e.target.value) }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              封面图片 URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              required
              value={formData.coverImage}
              onChange={(e) => setFormData(prev => ({ ...prev, coverImage: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="https://example.com/image.jpg"
            />
            {formData.coverImage && (
              <img
                src={formData.coverImage}
                alt="封面预览"
                className="mt-3 w-full h-48 object-cover rounded-lg"
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">标签</label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="输入标签后按回车"
              />
              <button
                type="button"
                onClick={addTag}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
              >
                添加
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.tags.map(tag => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm flex items-center gap-2"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="hover:text-teal-900"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">正文内容</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              rows={15}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none font-mono text-sm"
              placeholder="输入文章正文（支持 Markdown 格式）"
            />
          </div>
        </div>

        <div className="flex gap-4 mt-6">
          <button
            type="button"
            onClick={() => navigate('/admin/articles')}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            取消
          </button>
          <button
            type="submit"
            className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg hover:shadow-lg transition-all duration-300 whitespace-nowrap"
          >
            {isEdit ? '保存修改' : '发布文章'}
          </button>
        </div>
      </form>
    </div>
  );
}